/** A welcome rule as `GET /admin/welcome-rules` returns it. */
export interface WelcomeRule {
  id: string;
  /** Lowercase ISO 639 code this rule is written in, e.g. `en`, `ta`. */
  languageCode: string;
  title: string;
  content: string;
  /** Emoji shown beside the rule. */
  icon: string | null;
  /** Signed URL of the uploaded image (wins over `icon` in the app). */
  imageUrl: string | null;
  imageKey: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** What the banner needs to draw one rule. */
export type BannerRule = Pick<WelcomeRule, 'id' | 'title' | 'content' | 'icon' | 'imageUrl'>;

export interface RuleForm {
  languageCode: string;
  title: string;
  content: string;
  icon: string;
  imageKey: string | null;
  /** Preview of the image in `imageKey` (signed URL or a local object URL). */
  imageUrl: string | null;
  isActive: boolean;
}

export const DEFAULT_LANGUAGE = 'en';

/**
 * The Listener app's UI languages (maja_mobile `kAppLanguages`). Always offered
 * in the language picker so an admin can author a set before any rule exists
 * in it; the app asks for rules in its UI language and falls back to English.
 */
export const APP_UI_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'Tamil' },
  { code: 'hi', label: 'Hindi' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
];

/** `hi` → `hi · Hindi`; an unknown code is shown as-is. */
export function languageLabel(code: string): string {
  const known = APP_UI_LANGUAGES.find((l) => l.code === code);
  return known ? `${code} · ${known.label}` : code;
}

export const EMPTY_FORM: RuleForm = {
  languageCode: DEFAULT_LANGUAGE,
  title: '',
  content: '',
  icon: '',
  imageKey: null,
  imageUrl: null,
  isActive: true,
};

export const TITLE_MAX = 120;
export const CONTENT_MAX = 600;
export const IMAGE_TYPE_OPTIONS = [
  { value: 'image/png', label: 'PNG' },
  { value: 'image/jpeg', label: 'JPG' },
  { value: 'image/webp', label: 'WebP' },
];

/** Banner settings, as `GET/PUT /admin/welcome-rules/settings` exchanges them. */
export interface BannerSettings {
  /** Master switch — off hides the banner from every listener. */
  enabled: boolean;
  /** Max displays per listener per day. */
  dailyLimit: number;
  /** IANA timezone; empty = the app business timezone. */
  timezone: string;
  /** Minute of the local day the count resets at (0 = midnight). */
  dayStartMinute: number;
  showOnLaunch: boolean;
  showOnResume: boolean;
  /** Minutes in the background before a return counts as opening the app. */
  reopenAfterMinutes: number;
  /** A banner skipped because a call rang still uses up a display. */
  countSkippedDisplay: boolean;
  /** Emoji for rules with no icon/image; empty = a tick. */
  defaultIcon: string;
  maxImageKb: number;
  imageTypes: string[];
}

/** The GET also reports what is in effect right now. */
export interface BannerSettingsView extends BannerSettings {
  effectiveTimezone: string;
  businessDate: string;
  resetsAt: string;
}

export const DEFAULT_SETTINGS: BannerSettings = {
  enabled: true,
  dailyLimit: 2,
  timezone: '',
  dayStartMinute: 0,
  showOnLaunch: true,
  showOnResume: true,
  reopenAfterMinutes: 30,
  countSkippedDisplay: true,
  defaultIcon: '',
  maxImageKb: 1024,
  imageTypes: IMAGE_TYPE_OPTIONS.map((o) => o.value),
};

/** Only the editable fields — the PUT body is strict. */
export function settingsBody(s: BannerSettings): BannerSettings {
  return {
    enabled: s.enabled,
    dailyLimit: s.dailyLimit,
    timezone: s.timezone.trim(),
    dayStartMinute: s.dayStartMinute,
    showOnLaunch: s.showOnLaunch,
    showOnResume: s.showOnResume,
    reopenAfterMinutes: s.reopenAfterMinutes,
    countSkippedDisplay: s.countSkippedDisplay,
    defaultIcon: s.defaultIcon.trim(),
    maxImageKb: s.maxImageKb,
    imageTypes: s.imageTypes,
  };
}

/** The first problem with the settings, or null when they can be saved. */
export function settingsError(s: BannerSettings): string | null {
  if (!Number.isInteger(s.dailyLimit) || s.dailyLimit < 1 || s.dailyLimit > 50) {
    return 'Displays per day must be between 1 and 50.';
  }
  if (!Number.isInteger(s.reopenAfterMinutes) || s.reopenAfterMinutes < 0 || s.reopenAfterMinutes > 1440) {
    return 'Background minutes must be between 0 and 1440.';
  }
  if (!Number.isInteger(s.maxImageKb) || s.maxImageKb < 50 || s.maxImageKb > 5120) {
    return 'Image size limit must be between 50 and 5120 KB.';
  }
  if (s.imageTypes.length === 0) return 'Allow at least one image format.';
  if (!s.showOnLaunch && !s.showOnResume) {
    return 'Pick at least one moment to show the banner (launch or return).';
  }
  return null;
}

/** 480 → "08:00" (for a time input). */
export function minuteToTime(minute: number): string {
  const h = Math.floor(minute / 60);
  return `${String(h).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

/** "08:00" → 480; null when it is not a time. */
export function timeToMinute(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h > 23 || min > 59 ? null : h * 60 + min;
}

/** Why this file cannot be uploaded under the current settings, or null. */
export function imageProblem(file: { type: string; size: number }, s: BannerSettings): string | null {
  if (!s.imageTypes.includes(file.type)) {
    const allowed = IMAGE_TYPE_OPTIONS.filter((o) => s.imageTypes.includes(o.value)).map((o) => o.label);
    return `Use a ${allowed.join(', ')} image.`;
  }
  if (file.size > s.maxImageKb * 1024) return `Keep the image under ${s.maxImageKb} KB.`;
  return null;
}

export function formFromRule(rule: WelcomeRule): RuleForm {
  return {
    languageCode: rule.languageCode,
    title: rule.title,
    content: rule.content,
    icon: rule.icon ?? '',
    imageKey: rule.imageKey,
    imageUrl: rule.imageUrl,
    isActive: rule.isActive,
  };
}

/** Same rule the API enforces: a lowercase ISO 639 code, e.g. `ta`. */
export function isValidLanguageCode(code: string): boolean {
  return /^[a-z]{2,8}$/.test(code.trim().toLowerCase());
}

/** The first problem with the form, or null when it can be saved. */
export function formError(form: RuleForm): string | null {
  if (!isValidLanguageCode(form.languageCode)) {
    return 'Pick a language — a lowercase ISO 639 code like en or ta.';
  }
  const title = form.title.trim();
  if (title.length < 2) return 'Give the rule a title (at least 2 characters).';
  if (title.length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters.`;
  if (form.content.trim().length > CONTENT_MAX) return `Keep the description under ${CONTENT_MAX} characters.`;
  return null;
}

/** Request body for create / update. */
export function formBody(form: RuleForm) {
  return {
    languageCode: form.languageCode.trim().toLowerCase(),
    title: form.title.trim(),
    content: form.content.trim(),
    icon: form.icon.trim() || null,
    imageKey: form.imageKey,
    isActive: form.isActive,
  };
}

/** `ids` with `id` moved one place up (-1) or down (+1); unchanged at an edge. */
export function moveRule(ids: string[], id: string, direction: -1 | 1): string[] {
  const from = ids.indexOf(id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

/**
 * What the Listener app will show a listener whose primary language is
 * [language]: that language's active rules, or the whole English set when it
 * has none — the server's whole-set fallback, mirrored here so the preview
 * tells the truth.
 */
export function bannerRules(rules: WelcomeRule[], language = DEFAULT_LANGUAGE): BannerRule[] {
  const own = rules.filter((r) => r.isActive && r.languageCode === language);
  if (own.length > 0 || language === DEFAULT_LANGUAGE) return own;
  return rules.filter((r) => r.isActive && r.languageCode === DEFAULT_LANGUAGE);
}

/** Every language that has at least one rule, English first then A–Z. */
export function ruleLanguages(rules: WelcomeRule[]): string[] {
  const codes = [...new Set(rules.map((r) => r.languageCode))].sort();
  return codes.includes(DEFAULT_LANGUAGE)
    ? [DEFAULT_LANGUAGE, ...codes.filter((c) => c !== DEFAULT_LANGUAGE)]
    : codes;
}
